import moment from 'moment';
import type { SiteConfig } from 'src/contexts/SiteGuardContext';
import { BookingStatusType } from 'src/types/booking.type';
import { getSupportEmail } from 'src/utils/supportEmail.util';

/**
 * NPT-2081: the site's support phone and mailbox, trimmed. Both come from site config only. The
 * mailbox is the NPT-1983 traveller-facing `supportEmail`, resolved by getSupportEmail; `email` is
 * the SES sender (e.g. noreply@) and is only its fallback when `supportEmail` is blank, so never
 * read `email` directly here. An empty string means "not configured" and callers hide that line; never
 * fill in another site's number.
 */
export const getSiteSupportContact = (siteConfig?: Pick<SiteConfig, 'phoneNumber' | 'email' | 'supportEmail'> | null) => ({
   phone: siteConfig?.phoneNumber?.trim() || '',
   email: getSupportEmail(siteConfig),
});

/** `tel:` link for a display-formatted number such as "(800) 555-0100". */
export const telHref = (phone: string) => `tel:${phone.replace(/[^+\d]/g, '')}`;

type TripLike = { status?: string | null; checkoutDate?: string | null } | null | undefined;

/**
 * Which support tier leads on a trip's detail page. A live trip (not cancelled or voided, and
 * not checked out yet) is a stay context, so the 24/7 phone line leads. Past, cancelled and
 * undated trips only need receipts and general questions, so email leads.
 *
 * `checkoutDate` is compared by calendar day so the checkout day itself still counts as "during
 * the stay" whatever the traveller's time zone.
 */
export const getTripSupportEmphasis = (trip: TripLike, now: Date = new Date()): 'urgent' | 'standard' => {
   if (!trip) return 'standard';
   if (trip.status === BookingStatusType.CANCELLED || trip.status === BookingStatusType.VOIDED) return 'standard';
   if (!trip.checkoutDate) return 'standard';
   const checkoutDay = moment.utc(String(trip.checkoutDate).slice(0, 10), 'YYYY-MM-DD', true);
   if (!checkoutDay.isValid()) return 'standard';
   const today = moment(now).format('YYYY-MM-DD');
   return checkoutDay.format('YYYY-MM-DD') >= today ? 'urgent' : 'standard';
};
